import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { Mail, Plus, Save, Archive, ChevronDown, ChevronRight } from "lucide-react";

const inputCls = "mt-1 w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50";
const labelCls = "text-[10px] uppercase tracking-wider text-[#A8A29E]";

// Email Generator v2 (2026-02): founder/partner-only admin for the data-driven AI email
// templates (Opening/Intro+Estimate/Follow-up/Closing, extensible without code) + each
// template's founder-curated few-shot sample store. Samples are NEVER exposed to a PM, a
// client, or a partner anywhere else — read ONLY server-side by the compose endpoint.
export default function AdminEmailTemplates() {
  const { user } = useAuth();
  const canManage = user?.role === "founder" || user?.role === "partner";
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [samples, setSamples] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [newTemplate, setNewTemplate] = useState({ key: "", label: "", prompt_skeleton: "" });
  const [creating, setCreating] = useState(false);
  const [newSample, setNewSample] = useState({ subject: "", body_text: "" });

  const load = () => {
    setLoading(true);
    api.get("/v1/commercial/email-templates/admin").then((r) => setTemplates(r.data.items || [])).finally(() => setLoading(false));
  };
  useEffect(() => { if (canManage) load(); }, [canManage]);

  const loadSamples = (templateKey) => {
    api.get("/v1/commercial/email-samples", { params: { template_key: templateKey } }).then((r) => {
      setSamples((prev) => ({ ...prev, [templateKey]: r.data.items || [] }));
    });
  };

  const toggleExpand = (key) => {
    if (expanded === key) { setExpanded(null); return; }
    setExpanded(key);
    if (!samples[key]) loadSamples(key);
  };

  const savePrompt = async (tpl) => {
    setSavingId(tpl.id);
    try {
      await api.patch(`/v1/commercial/email-templates/${tpl.id}`, { label: tpl.label, prompt_skeleton: tpl.prompt_skeleton });
      toast.success("Template saved");
      load();
    } catch { toast.error("Couldn't save template"); } finally { setSavingId(null); }
  };

  const createTemplate = async () => {
    if (!newTemplate.key.trim() || !newTemplate.label.trim() || !newTemplate.prompt_skeleton.trim()) {
      toast.error("Key, label, and prompt are all required"); return;
    }
    setCreating(true);
    try {
      await api.post("/v1/commercial/email-templates", newTemplate);
      toast.success("Template created");
      setNewTemplate({ key: "", label: "", prompt_skeleton: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code === "template_key_exists" ? "That key already exists" : "Couldn't create template");
    } finally { setCreating(false); }
  };

  const addSample = async (templateKey) => {
    if (!newSample.body_text.trim()) { toast.error("Sample body is required"); return; }
    try {
      await api.post("/v1/commercial/email-samples", { template_key: templateKey, ...newSample });
      setNewSample({ subject: "", body_text: "" });
      loadSamples(templateKey);
      toast.success("Sample added");
    } catch { toast.error("Couldn't add sample"); }
  };

  const archiveSample = async (templateKey, sampleId) => {
    await api.patch(`/v1/commercial/email-samples/${sampleId}`, { active: false });
    loadSamples(templateKey);
  };

  if (!canManage) {
    return <div className="p-8 text-sm text-[#57534E]" data-testid="email-templates-denied">Only the founder or partner can manage Email Templates.</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-4" data-testid="admin-email-templates-page">
      <div className="flex items-center gap-2">
        <Mail className="h-5 w-5 text-[#B45309]" />
        <h1 className="text-lg font-medium text-[#1C1C1E]">Email Templates & Voice Samples</h1>
      </div>
      <p className="text-xs text-[#57534E]">Each template's prompt controls what the AI writes in the Quote Builder's Draft Email panel — never prices/numbers, always PM-reviewed before sending. Samples below are internal voice reference only, few-shotted into the AI call, never shown to a client or partner.</p>

      {loading ? (
        <div className="text-xs text-[#A8A29E]">Loading…</div>
      ) : (
        <div className="space-y-2" data-testid="email-templates-list">
          {templates.map((tpl) => (
            <div key={tpl.id} className={`rounded-lg border ${tpl.active === false ? "border-[#E7E5E4] opacity-60" : "border-[#E7E5E4]"} bg-[#FFFFFF]`} data-testid={`email-template-row-${tpl.key}`}>
              <button onClick={() => toggleExpand(tpl.key)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid={`email-template-toggle-${tpl.key}`}>
                <div className="flex items-center gap-2">
                  {expanded === tpl.key ? <ChevronDown className="h-3.5 w-3.5 text-[#A8A29E]" /> : <ChevronRight className="h-3.5 w-3.5 text-[#A8A29E]" />}
                  <span className="text-sm text-[#1C1C1E]">{tpl.label}</span>
                  <span className="text-[10px] text-[#A8A29E] font-mono">{tpl.key}</span>
                  {tpl.active === false && <span className="rounded-full bg-[#F5F5F4] px-2 py-0.5 text-[9px] text-[#A8A29E]">inactive</span>}
                </div>
              </button>
              {expanded === tpl.key && (
                <div className="border-t border-[#E7E5E4] px-4 py-3 space-y-3">
                  <div>
                    <label className={labelCls}>Prompt Skeleton</label>
                    <textarea data-testid={`email-template-prompt-${tpl.key}`} value={tpl.prompt_skeleton} rows={6}
                      onChange={(e) => setTemplates((prev) => prev.map((t) => t.id === tpl.id ? { ...t, prompt_skeleton: e.target.value } : t))}
                      className={inputCls} />
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`email-template-save-${tpl.key}`} onClick={() => savePrompt(tpl)} disabled={savingId === tpl.id}
                      className="inline-flex items-center gap-1.5 rounded-md bg-[#1C1C1E] px-3 py-1.5 text-xs text-white hover:bg-[#1C1C1E]/90 disabled:opacity-50">
                      <Save className="h-3 w-3" /> {savingId === tpl.id ? "Saving…" : "Save Prompt"}
                    </button>
                    <button data-testid={`email-template-archive-${tpl.key}`}
                      onClick={async () => { await api.patch(`/v1/commercial/email-templates/${tpl.id}`, { active: !(tpl.active === false) }); load(); }}
                      className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs text-[#57534E] hover:bg-[#F5F5F4]">
                      <Archive className="h-3 w-3" /> {tpl.active === false ? "Reactivate" : "Deactivate"}
                    </button>
                  </div>

                  <div className="border-t border-[#E7E5E4] pt-3">
                    <label className={labelCls}>Voice Samples ({(samples[tpl.key] || []).filter((s) => s.active !== false).length} active)</label>
                    <div className="mt-1.5 space-y-1.5">
                      {(samples[tpl.key] || []).filter((s) => s.active !== false).map((s) => (
                        <div key={s.id} data-testid={`email-sample-row-${s.id}`} className="rounded-md border border-[#E7E5E4] px-2.5 py-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-[#1C1C1E]">{s.subject || "(no subject)"}</span>
                            <button data-testid={`email-sample-archive-${s.id}`} onClick={() => archiveSample(tpl.key, s.id)} className="text-[10px] text-[#A8A29E] hover:text-red-600">Archive</button>
                          </div>
                          <p className="text-[11px] text-[#57534E] whitespace-pre-wrap mt-1">{s.body_text}</p>
                        </div>
                      ))}
                      {(samples[tpl.key] || []).filter((s) => s.active !== false).length === 0 && (
                        <div className="text-[11px] text-[#A8A29E]" data-testid={`email-samples-empty-${tpl.key}`}>No samples yet — the AI will write from the prompt alone.</div>
                      )}
                    </div>
                    <div className="mt-2 space-y-1.5">
                      <input data-testid={`email-sample-subject-input-${tpl.key}`} placeholder="Subject (optional)" value={newSample.subject}
                        onChange={(e) => setNewSample((p) => ({ ...p, subject: e.target.value }))} className={inputCls} />
                      <textarea data-testid={`email-sample-body-input-${tpl.key}`} placeholder="Paste a real sent email body here…" rows={4} value={newSample.body_text}
                        onChange={(e) => setNewSample((p) => ({ ...p, body_text: e.target.value }))} className={inputCls} />
                      <button data-testid={`email-sample-add-btn-${tpl.key}`} onClick={() => addSample(tpl.key)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-[#FFC817]/40 bg-[#FFC817]/10 px-3 py-1.5 text-xs text-[#B45309] hover:bg-[#FFC817]/20">
                        <Plus className="h-3 w-3" /> Add Sample
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-4 space-y-2">
        <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Add a New Template</div>
        <input data-testid="new-email-template-key-input" placeholder="key (e.g. thank_you_final)" value={newTemplate.key}
          onChange={(e) => setNewTemplate((p) => ({ ...p, key: e.target.value }))} className={inputCls} />
        <input data-testid="new-email-template-label-input" placeholder="Label (e.g. Thank You — Final)" value={newTemplate.label}
          onChange={(e) => setNewTemplate((p) => ({ ...p, label: e.target.value }))} className={inputCls} />
        <textarea data-testid="new-email-template-prompt-input" placeholder="Prompt skeleton…" rows={4} value={newTemplate.prompt_skeleton}
          onChange={(e) => setNewTemplate((p) => ({ ...p, prompt_skeleton: e.target.value }))} className={inputCls} />
        <button data-testid="new-email-template-create-btn" onClick={createTemplate} disabled={creating}
          className="inline-flex items-center gap-1.5 rounded-md bg-[#1C1C1E] px-4 py-2 text-xs text-white hover:bg-[#1C1C1E]/90 disabled:opacity-50">
          <Plus className="h-3.5 w-3.5" /> {creating ? "Creating…" : "Create Template"}
        </button>
      </div>
    </div>
  );
}
